import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import { CARRIERS_BY_TYPE } from "../../data/carriersByType";
import adminFetch from "../../utils/adminFetch";
import { useSocket } from "../../hooks/useSocket";
import AssignmentDropdown from "../../components/admin/AssignmentDropdown";
import ActivityNotes from "../../components/admin/ActivityNotes";

// ── Helpers ───────────────────────────────────────────────────────────

function formatDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function timeAgo(str) {
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return formatDate(str);
}

// ── Config ────────────────────────────────────────────────────────────
const INSURANCE_LABELS = {
  home: "Home", auto: "Auto", life: "Life", business: "Business",
  health: "Health", travel: "Travel", renters: "Renters",
  umbrella: "Umbrella", disability: "Disability", flood: "Flood",
  identity: "Identity Theft",
};

const STATUS_OPTIONS = ["pending", "reviewed", "quoted", "closed"];
const STATUS_CONFIG  = {
  pending:  { label: "Pending",  color: "#92400E", bg: "#FEF3C7" },
  reviewed: { label: "Reviewed", color: "#1D4ED8", bg: "#DBEAFE" },
  quoted:   { label: "Quoted",   color: "#065F46", bg: "#D1FAE5" },
  closed:   { label: "Closed",   color: "#6B7280", bg: "#F3F4F6" },
};

const PAGE_SIZE = 15;

// ── Small components ──────────────────────────────────────────────────
function Spinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.4)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div>
      <p className="font-cinzel uppercase text-[#012E72] mb-2.5"
        style={{ fontSize: "9px", letterSpacing: "0.2em", opacity: 0.5 }}>
        {title}
      </p>
      <div className="flex flex-col gap-0">
        {children}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  const display = value === true ? "Yes" : value === false ? "No" : String(value);
  return (
    <div className="flex gap-3 py-1.5 border-b border-gray-50 last:border-0">
      <span className="font-cinzel uppercase text-gray-400 flex-shrink-0"
        style={{ fontSize: "8px", letterSpacing: "0.12em", paddingTop: "2px", width: "110px" }}>
        {label}
      </span>
      <span className="font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>
        {display}
      </span>
    </div>
  );
}

// ── Carrier logo button ───────────────────────────────────────────────
function getInitials(name) {
  const words = name.split(/[\s&/]+/).filter(
    (w) => w.length > 1 && !/^(and|the|of|for|by|at)$/i.test(w)
  );
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase();
}

function CarrierButton({ carrier }) {
  const [srcIdx,  setSrcIdx]  = useState(0);
  const [hovered, setHovered] = useState(false);

  let domain = "";
  try {
    const hostname = new URL(carrier.url).hostname.replace(/^www\./, "");
    domain = carrier.logoDomain || hostname;
  } catch {}

  const sources = [
    carrier.logo,
    domain ? `https://logo.clearbit.com/${domain}` : null,
    domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null,
  ].filter(Boolean);

  const showInitials = srcIdx >= sources.length;

  return (
    <a
      href={carrier.url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "88px",
        textDecoration: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        padding: "10px 6px",
        borderRadius: "12px",
        border: carrier.priority
          ? `1.5px solid ${hovered ? "rgba(0,45,181,0.5)" : "rgba(0,45,181,0.22)"}`
          : `1.5px solid ${hovered ? "rgba(1,46,114,0.2)" : "rgba(0,0,0,0.08)"}`,
        backgroundColor: hovered
          ? carrier.priority ? "rgba(0,45,181,0.08)" : "rgba(1,46,114,0.04)"
          : carrier.priority ? "rgba(0,45,181,0.03)" : "#fff",
        boxShadow: hovered ? "0 4px 14px rgba(0,0,0,0.10)" : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.18s ease",
        cursor: "pointer",
      }}
    >
      <div style={{
        width: "40px", height: "40px",
        borderRadius: "8px",
        border: "1px solid rgba(0,0,0,0.07)",
        backgroundColor: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", flexShrink: 0,
      }}>
        {!showInitials ? (
          <img
            src={sources[srcIdx]}
            alt={carrier.name}
            onError={() => setSrcIdx((i) => i + 1)}
            style={{ width: "30px", height: "30px", objectFit: "contain" }}
          />
        ) : (
          <span style={{
            fontFamily: "Cinzel, serif", fontWeight: 700,
            fontSize: "11px", color: "#012E72", letterSpacing: "0.05em",
          }}>
            {getInitials(carrier.name)}
          </span>
        )}
      </div>

      <span style={{
        fontFamily: "Constantia, serif",
        fontSize: "9.5px",
        color: hovered ? "#012E72" : "#374151",
        textAlign: "center",
        lineHeight: 1.3,
        wordBreak: "break-word",
      }}>
        {carrier.name}
      </span>

      {carrier.priority && (
        <span style={{ fontSize: "8px", color: "#002DB5", marginTop: "-2px" }}>★ Priority</span>
      )}
    </a>
  );
}

// ── AI Carrier Suggestions ────────────────────────────────────────────
const CONFIDENCE_CONFIG = {
  high:   { label: "High Match",   color: "#065F46", bg: "#D1FAE5" },
  medium: { label: "Good Match",   color: "#1D4ED8", bg: "#DBEAFE" },
  low:    { label: "Possible",     color: "#6B7280", bg: "#F3F4F6" },
};

function AICarrierSuggestions({ quoteId }) {
  const [status,   setStatus]   = useState("idle");
  const [result,   setResult]   = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [fromCache,    setFromCache]    = useState(false);
  const [cachedAt,     setCachedAt]     = useState(null);

  const fetchSuggestions = async (force = false) => {
    setStatus("loading");
    setResult(null);
    setErrorMsg("");
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/admin/quotes/${quoteId}/suggest-carriers${force ? "?force=true" : ""}`;
      const res  = await adminFetch(url, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Request failed.");
      setResult(json.data);
      setFromCache(json.cached === true);
      setCachedAt(json.generatedAt || null);
      setStatus("done");
    } catch (err) {
      setErrorMsg(err.message);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (quoteId) fetchSuggestions();
  }, [quoteId]);

  return (
    <div style={{ borderTop: "1.5px solid rgba(0,45,181,0.12)", padding: "22px 32px 26px", backgroundColor: "#f0f4fa" }}>

      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
          </svg>
          <p className="font-cinzel font-bold uppercase"
            style={{ fontSize: "10px", letterSpacing: "0.2em", color: "#6D28D9" }}>
            AI Smart Carrier Pick
          </p>
          <span className="font-cinzel uppercase px-2 py-0.5 rounded-full"
            style={{ fontSize: "7px", letterSpacing: "0.12em", backgroundColor: "#EDE9FE", color: "#6D28D9" }}>
            Claude AI
          </span>
        </div>

        {status === "done" && (
          <div className="flex items-center gap-3">
            {fromCache && cachedAt && (
              <span className="font-constantia italic text-gray-400" style={{ fontSize: "11px" }}>
                Cached · {timeAgo(cachedAt)}
              </span>
            )}
            <button
              onClick={() => fetchSuggestions(true)}
              className="flex items-center gap-2 font-cinzel uppercase tracking-widest transition-all duration-200"
              style={{
                fontSize: "9px", letterSpacing: "0.15em",
                padding: "7px 14px", borderRadius: "20px",
                backgroundColor: "rgba(109,40,217,0.07)",
                color: "#6D28D9",
                border: "1.5px solid rgba(109,40,217,0.25)",
              }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Re-analyze
            </button>
          </div>
        )}
      </div>

      {/* Loading state */}
      {status === "loading" && (
        <div className="flex items-center gap-3 py-4">
          <svg className="animate-spin flex-shrink-0" width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="#6D28D9" strokeWidth="2.5">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <p className="font-constantia italic text-gray-500" style={{ fontSize: "13px" }}>
            Claude is analyzing this quote and selecting the best carriers…
          </p>
        </div>
      )}

      {/* Error state */}
      {status === "error" && (
        <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg"
          style={{ backgroundColor: "#FEF2F2", border: "1.5px solid #FECACA" }}>
          <p className="font-constantia text-red-700" style={{ fontSize: "12.5px" }}>
            {errorMsg || "Something went wrong. Please try again."}
          </p>
          <button onClick={fetchSuggestions}
            className="font-cinzel uppercase tracking-widest text-red-600 border border-red-300 px-3 py-1.5 rounded-full hover:bg-red-50 transition-colors flex-shrink-0"
            style={{ fontSize: "8px", letterSpacing: "0.12em" }}>
            Retry
          </button>
        </div>
      )}

      {/* Results */}
      {status === "done" && result && (
        <div className="flex flex-col gap-4">
          {result.summary && (
            <div className="flex gap-3 px-4 py-3 rounded-xl"
              style={{ backgroundColor: "rgba(109,40,217,0.06)", border: "1.5px solid rgba(109,40,217,0.15)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6D28D9" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="font-constantia italic text-[#4C1D95]" style={{ fontSize: "13px", lineHeight: 1.6 }}>
                {result.summary}
              </p>
            </div>
          )}

          {(result.recommendations || []).length > 0 && (
            <div className="flex flex-col gap-2.5">
              {result.recommendations.map((rec, i) => {
                const conf = CONFIDENCE_CONFIG[rec.confidence] || CONFIDENCE_CONFIG.low;
                return (
                  <div key={i}
                    className="flex items-start gap-4 px-4 py-3.5 rounded-xl"
                    style={{ backgroundColor: "white", border: "1.5px solid rgba(1,46,114,0.09)" }}>
                    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: "rgba(109,40,217,0.10)", marginTop: "1px" }}>
                      <span className="font-cinzel font-bold text-[#6D28D9]" style={{ fontSize: "9px" }}>
                        {i + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <a href={rec.url} target="_blank" rel="noopener noreferrer"
                          className="font-cinzel font-bold text-[#012E72] hover:text-[#6D28D9] transition-colors"
                          style={{ fontSize: "12px", letterSpacing: "0.04em" }}>
                          {rec.carrier}
                        </a>
                        <span className="font-cinzel uppercase px-2 py-0.5 rounded-full text-[#012E72]"
                          style={{ fontSize: "7px", letterSpacing: "0.1em", backgroundColor: "rgba(0,45,181,0.08)" }}>
                          {INSURANCE_LABELS[rec.insuranceType] || rec.insuranceType}
                        </span>
                        <span className="font-cinzel uppercase px-2 py-0.5 rounded-full"
                          style={{ fontSize: "7px", letterSpacing: "0.1em", color: conf.color, backgroundColor: conf.bg }}>
                          {conf.label}
                        </span>
                        {rec.url && (
                          <a href={rec.url} target="_blank" rel="noopener noreferrer"
                            className="text-gray-300 hover:text-[#6D28D9] transition-colors"
                            title="Open carrier portal">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                              <polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" />
                            </svg>
                          </a>
                        )}
                      </div>
                      {rec.reason && (
                        <p className="font-constantia text-gray-500" style={{ fontSize: "12.5px", lineHeight: 1.6 }}>
                          {rec.reason}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <p className="font-constantia text-gray-400" style={{ fontSize: "10.5px" }}>
            AI suggestions are based on the quote details and known carrier eligibility. Always verify with carrier portals.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Suggested Carriers ────────────────────────────────────────────────
function SuggestedCarriers({ insuranceTypes, quoteId }) {
  if (!insuranceTypes || insuranceTypes.length === 0) return null;

  const seen = new Set();
  const allCarriers = [];
  insuranceTypes.forEach((type) => {
    (CARRIERS_BY_TYPE[type] || []).forEach((c) => {
      if (!seen.has(c.url)) { seen.add(c.url); allCarriers.push(c); }
    });
  });

  const priority   = allCarriers.filter((c) => c.priority);
  const additional = allCarriers.filter((c) => !c.priority);

  if (allCarriers.length === 0) return null;

  return (
    <div>
      <AICarrierSuggestions quoteId={quoteId} />

      <div style={{ backgroundColor: "#f7f9ff", borderTop: "2px solid rgba(1,46,114,0.10)", padding: "24px 32px 28px" }}>
        <div className="flex items-center gap-2 mb-5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="#012E72" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <p className="font-cinzel font-bold uppercase text-[#012E72]"
            style={{ fontSize: "10px", letterSpacing: "0.2em" }}>
            Suggested Carriers — Quote This At
          </p>
          <span className="font-constantia text-gray-400 ml-1" style={{ fontSize: "11px" }}>
            ({priority.length} priority · {additional.length} additional)
          </span>
        </div>

        {priority.length > 0 && (
          <div className="mb-5">
            <p className="font-cinzel uppercase text-[#002DB5] mb-3"
              style={{ fontSize: "8px", letterSpacing: "0.18em", opacity: 0.7 }}>
              ★ Priority Carriers
            </p>
            <div className="flex flex-wrap gap-3">
              {priority.map((c, i) => <CarrierButton key={`p-${i}`} carrier={c} />)}
            </div>
          </div>
        )}

        {additional.length > 0 && (
          <div>
            <p className="font-cinzel uppercase text-gray-400 mb-3"
              style={{ fontSize: "8px", letterSpacing: "0.18em" }}>
              Additional Carriers
            </p>
            <div className="flex flex-wrap gap-3">
              {additional.map((c, i) => <CarrierButton key={`a-${i}`} carrier={c} />)}
            </div>
          </div>
        )}

        <p className="font-constantia text-gray-400 mt-4" style={{ fontSize: "10.5px" }}>
          Some carrier portals may require VPN access. Click any logo to open the carrier portal in a new tab.
        </p>
      </div>
    </div>
  );
}

// ── Agent Notes ───────────────────────────────────────────────────────
function AgentNotes({ quoteId, initialNotes = [] }) {
  const [notes,   setNotes]   = useState(initialNotes);
  const [text,    setText]    = useState("");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [deleting, setDeleting] = useState(null);

  function formatNoteDate(str) {
    return new Date(str).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  }

  const handleAdd = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes/${quoteId}/notes`, {
        method: "POST",
        body:   JSON.stringify({ text: text.trim() }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Failed to save note.");
      setNotes(json.data);
      setText("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeleting(noteId);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes/${quoteId}/notes/${noteId}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Delete failed.");
      setNotes(json.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const sorted = [...notes].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <div
      className="px-8 py-6"
      style={{ backgroundColor: "#f7f9ff", borderTop: "1px solid rgba(1,46,114,0.08)" }}
    >
      <p className="font-cinzel uppercase text-[#012E72] mb-4"
        style={{ fontSize: "9px", letterSpacing: "0.2em" }}>
        Agent Notes
      </p>

      {sorted.length > 0 ? (
        <div className="flex flex-col gap-3 mb-5">
          {sorted.map((n) => (
            <div key={n._id} className="bg-white rounded-xl px-5 py-4 flex items-start gap-3"
              style={{ border: "1px solid rgba(1,46,114,0.1)" }}>
              <div className="flex-1">
                <p className="font-constantia text-[#012E72] leading-relaxed mb-1.5" style={{ fontSize: "14px" }}>
                  {n.text}
                </p>
                <p className="font-cinzel uppercase text-gray-400" style={{ fontSize: "8px", letterSpacing: "0.12em" }}>
                  {formatNoteDate(n.createdAt)}
                </p>
              </div>
              <button
                onClick={() => handleDelete(n._id)}
                disabled={deleting === n._id}
                className="flex-shrink-0 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-40"
                title="Delete note"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="font-constantia text-gray-400 italic mb-5" style={{ fontSize: "13px" }}>
          No notes yet.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <textarea
          rows={3}
          placeholder="Add a note for this quote…"
          value={text}
          onChange={(e) => { setText(e.target.value); setError(null); }}
          className="font-constantia rounded-xl px-4 py-3 outline-none resize-none transition-all duration-200 w-full"
          style={{
            fontSize: "14px", color: "#012E72",
            border: "1.5px solid rgba(1,46,114,0.15)",
            backgroundColor: "white",
          }}
          onFocus={(e) => (e.target.style.borderColor = "#012E72")}
          onBlur={(e)  => (e.target.style.borderColor = "rgba(1,46,114,0.15)")}
        />
        {error && (
          <p className="font-constantia text-red-600" style={{ fontSize: "12px" }}>{error}</p>
        )}
        <div>
          <button
            onClick={handleAdd}
            disabled={!text.trim() || saving}
            className="font-cinzel uppercase tracking-widest rounded-full px-6 py-2.5 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              fontSize: "9px", letterSpacing: "0.15em",
              backgroundColor: "#012E72", color: "white",
            }}
          >
            {saving ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Expanded row detail panel ─────────────────────────────────────────
function ExpandedQuote({ q }) {
  const insuranceTypes = q.selectedInsurance || [];
  const claims = (q.claims || []).filter((c) => c.date || c.type || c.amount);
  const [assignedTo, setAssignedTo] = useState(q.assignedTo || {});

  return (
    <tr>
      <td colSpan={9} style={{ padding: 0 }}>
        <div
          className="px-8 py-7 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8"
          style={{ backgroundColor: "#f0f4fa", borderTop: "1px solid rgba(1,46,114,0.08)", borderBottom: "1px solid rgba(1,46,114,0.08)" }}
        >
          {/* Contact */}
          <DetailSection title="Contact & Address">
            <DetailRow label="Full Name"    value={[q.firstName, q.middleName, q.lastName].filter(Boolean).join(" ")} />
            <DetailRow label="Date of Birth" value={q.dob} />
            <DetailRow label="Phone"        value={q.phone} />
            <DetailRow label="Email"        value={q.email} />
            <DetailRow label="Street"       value={q.street} />
            <DetailRow label="City / State" value={q.city ? `${q.city}, ${q.state} ${q.zip}` : null} />
          </DetailSection>

          {/* Demographics */}
          <DetailSection title="Demographics">
            <DetailRow label="SSN Last 4"    value={q.ssn4} />
            <DetailRow label="Full SSN"       value={q.ssnFull} />
            <DetailRow label="Marital Status" value={q.maritalStatus} />
            <DetailRow label="Gender"         value={q.gender} />
            <DetailRow label="Language"       value={q.language} />
          </DetailSection>

          {/* Prior Insurance */}
          <DetailSection title="Prior Insurance">
            <DetailRow label="Prior Carrier" value={q.priorCarrier} />
            <DetailRow label="Exp / Cancel"  value={q.priorExpDate} />
            <DetailRow label="Cancel Reason" value={q.cancelReason} />
            <DetailRow label="Details"       value={q.cancelDetails} />
          </DetailSection>

          {/* Claims History */}
          <DetailSection title="Claims History (Past 5 Years)">
            {claims.length === 0 ? (
              <p className="font-constantia text-gray-400 italic" style={{ fontSize: "13px" }}>
                No claims reported.
              </p>
            ) : (
              <div className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(1,46,114,0.1)" }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: "rgba(1,46,114,0.05)" }}>
                      {["Date", "Type", "Paid Amount"].map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-cinzel uppercase text-gray-400"
                          style={{ fontSize: "8px", letterSpacing: "0.12em" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((c, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: "rgba(1,46,114,0.06)" }}>
                        <td className="px-3 py-2 font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>{c.date   || "—"}</td>
                        <td className="px-3 py-2 font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>{c.type   || "—"}</td>
                        <td className="px-3 py-2 font-constantia text-[#012E72]" style={{ fontSize: "13px" }}>{c.amount ? `$${Number(c.amount).toLocaleString()}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </DetailSection>

          {/* Consent & Signature */}
          <DetailSection title="Consent & Signature">
            <DetailRow label="Credit Consent"   value={q.consentCredit} />
            <DetailRow label="Delivery Consent" value={q.consentDelivery} />
            <DetailRow label="Signature"        value={q.signature} />
            <DetailRow label="Referral Code"    value={q.referral} />
          </DetailSection>

          {/* Insurance Lines & Notes */}
          <DetailSection title="Insurance Lines & Notes">
            {insuranceTypes.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {insuranceTypes.map((id) => (
                  <span key={id}
                    className="font-cinzel uppercase px-2.5 py-1 rounded-full text-[#012E72]"
                    style={{ fontSize: "8px", letterSpacing: "0.1em", backgroundColor: "rgba(0,45,181,0.08)" }}>
                    {INSURANCE_LABELS[id] || id}
                  </span>
                ))}
              </div>
            )}
            <DetailRow label="Notes"     value={q.notes} />
            <DetailRow label="Submitted" value={formatDate(q.createdAt)} />
          </DetailSection>
        </div>

        {/* Agent Notes */}
        <AgentNotes quoteId={q._id} initialNotes={q.agentNotes || []} />

        {/* Assignment + Activity Notes */}
        <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "16px" }}>
          <AssignmentDropdown
            entityType="quotes"
            entityId={q._id}
            assignedTo={assignedTo}
            onAssigned={setAssignedTo}
          />
          <ActivityNotes
            entityType="quotes"
            entityId={q._id}
            notes={q.activityNotes || []}
          />
        </div>

        {/* Suggested Carriers */}
        <SuggestedCarriers insuranceTypes={insuranceTypes} quoteId={q._id} />
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function AdminQuotes() {
  usePageTitle("Quote Requests");
  const navigate   = useNavigate();
  const socketRef  = useSocket();
  const [quotes,     setQuotes]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");
  const [expanded,   setExpanded]   = useState(null);
  const [updating,   setUpdating]   = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [counts,     setCounts]     = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const searchTimer = useRef(null);

  const loadQuotes = useCallback(async (pg, srch, flt, from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (srch) params.set("search", srch);
      if (flt && flt !== "all") params.set("status", flt);
      if (from) params.set("from", from);
      if (to)   params.set("to", to);
      const res = await adminFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/quotes?${params}`
      );
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Failed to load quotes.");
      const json = await res.json();
      setQuotes(json.data);
      setTotal(json.total);
      setTotalPages(json.pages);
      setCounts(json.counts || {});
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadQuotes(1, "", "all", "", ""); }, [loadQuotes]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); loadQuotes(1, search, filter, dateFrom, dateTo); }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadQuotes(page, search, filter, dateFrom, dateTo); }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); loadQuotes(1, search, filter, dateFrom, dateTo); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    function onNew(doc) {
      if (page === 1 && !search && filter === "all" && !dateFrom && !dateTo) {
        setQuotes(prev => {
          if (prev.some(q => q._id === doc._id)) return prev;
          return [doc, ...prev].slice(0, PAGE_SIZE);
        });
        setTotal(prev => prev + 1);
        setCounts(prev => ({ ...prev, pending: (prev.pending || 0) + 1 }));
      } else {
        loadQuotes(page, search, filter, dateFrom, dateTo);
      }
    }

    function onUpdated({ id, status }) {
      setQuotes(prev => prev.map(q => q._id === id ? { ...q, status } : q));
      loadQuotes(page, search, filter, dateFrom, dateTo);
    }

    function onDeleted({ id }) {
      setQuotes(prev => prev.filter(q => q._id !== id));
      setExpanded(prev => prev === id ? null : prev);
      setTotal(prev => Math.max(0, prev - 1));
    }

    socket.on("quote:new",     onNew);
    socket.on("quote:updated", onUpdated);
    socket.on("quote:deleted", onDeleted);

    return () => {
      socket.off("quote:new",     onNew);
      socket.off("quote:updated", onUpdated);
      socket.off("quote:deleted", onDeleted);
    };
  }, [socketRef, page, search, filter, dateFrom, dateTo, loadQuotes]);

  const updateStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes/${id}`, {
        method: "PATCH",
        body:   JSON.stringify({ status }),
      });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Status update failed.");
      loadQuotes(page, search, filter, dateFrom, dateTo);
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote request? This cannot be undone.")) return;
    setDeleting(id);
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes/${id}`, {
        method: "DELETE",
      });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Delete failed.");
      if (expanded === id) setExpanded(null);
      loadQuotes(page, search, filter, dateFrom, dateTo);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(null);
    }
  };

  const exportCSV = async () => {
    const params = new URLSearchParams({ page: 1, limit: 1000 });
    if (search) params.set("search", search);
    if (filter && filter !== "all") params.set("status", filter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo)   params.set("to", dateTo);
    const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/quotes?${params}`);
    const json = await res.json();
    const all  = json.data || [];
    const headers = ["First Name","Last Name","Email","Phone","DOB","Street","City","State","ZIP",
                     "Insurance Types","Prior Carrier","Cancel Reason","Marital Status","Gender","Language",
                     "Status","Referral","Notes","Submitted"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = all.map((q) => [
      q.firstName, q.lastName, q.email, q.phone, q.dob,
      q.street, q.city, q.state, q.zip,
      (q.selectedInsurance || []).join("; "),
      q.priorCarrier, q.cancelReason, q.maritalStatus, q.gender, q.language,
      q.status, q.referral, q.notes,
      new Date(q.createdAt).toLocaleDateString("en-US"),
    ].map(escape).join(","));
    const csv  = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `paladin-quotes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const paginated = quotes;
  const safePage  = page;

  if (loading) return <Spinner />;
  if (error) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <p className="font-constantia text-red-600">{error}</p>
      <button onClick={() => window.location.reload()}
        className="font-cinzel uppercase tracking-widest text-[#012E72] border border-[#012E72] border-opacity-30 px-5 py-2 rounded-full text-xs hover:bg-[#012E72] hover:text-white transition-colors">
        Retry
      </button>
    </div>
  );

  return (
    <div className="flex flex-col gap-5 max-w-7xl">

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-cinzel font-bold text-[#012E72] uppercase"
            style={{ fontSize: "22px", letterSpacing: "0.06em" }}>
            Quote Requests
          </h2>
          <p className="font-constantia text-gray-400 mt-0.5" style={{ fontSize: "13px" }}>
            {total} total · {counts.pending || 0} pending
          </p>
        </div>
        {quotes.length > 0 && (
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 font-cinzel uppercase tracking-widest border rounded-full px-5 py-2.5 transition-all duration-200 hover:bg-[#012E72] hover:text-white hover:border-[#012E72] flex-shrink-0"
            style={{ fontSize: "9px", letterSpacing: "0.15em", color: "#012E72", borderColor: "rgba(1,46,114,0.2)" }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
        )}
      </div>

      {/* Search + filter row */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" width="13" height="13"
            viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full font-constantia pl-8 pr-3 py-2 rounded-full outline-none"
            style={{
              fontSize: "13px", color: "#012E72",
              border: "1.5px solid rgba(1,46,114,0.12)",
              backgroundColor: "white",
            }}
          />
          {search && (
            <button onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors"
              style={{ lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="font-constantia text-[#012E72] rounded-full outline-none px-3 py-2"
            style={{ fontSize: "12px", border: "1.5px solid rgba(1,46,114,0.12)", backgroundColor: "white", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.12)"} />
          <span className="font-cinzel text-gray-300" style={{ fontSize: "9px" }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="font-constantia text-[#012E72] rounded-full outline-none px-3 py-2"
            style={{ fontSize: "12px", border: "1.5px solid rgba(1,46,114,0.12)", backgroundColor: "white", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.12)"} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="font-cinzel uppercase text-gray-300 hover:text-gray-500 transition-colors" style={{ fontSize: "9px", letterSpacing: "0.1em", background: "none", border: "none", cursor: "pointer" }}>×</button>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {["all", ...STATUS_OPTIONS].map((s) => {
            const active = filter === s;
            const count  = s === "all" ? total : counts[s] || 0;
            return (
              <button key={s} onClick={() => setFilter(s)}
                className="font-cinzel uppercase tracking-widest px-4 py-2 rounded-full transition-all duration-200"
                style={{
                  fontSize: "9px", letterSpacing: "0.15em",
                  backgroundColor: active ? "#012E72" : "white",
                  color: active ? "white" : "#6B7280",
                  border: `1.5px solid ${active ? "#012E72" : "rgba(1,46,114,0.12)"}`,
                }}>
                {s === "all" ? "All" : STATUS_CONFIG[s].label}
                {count > 0 && <span className="ml-1.5 opacity-70">({count})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden" style={{ border: "1.5px solid rgba(1,46,114,0.08)" }}>
        {paginated.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-2">
            <p className="font-constantia text-gray-400" style={{ fontSize: "14px" }}>
              {search ? `No results for "${search}".` : "No quote requests found."}
            </p>
            {search && (
              <button onClick={() => setSearch("")}
                className="font-cinzel uppercase tracking-widest text-[#012E72] text-xs underline underline-offset-2 opacity-60 hover:opacity-100 transition-opacity">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: "#f7f9ff", borderBottom: "1.5px solid rgba(1,46,114,0.07)" }}>
                  {["Name", "DOB", "Phone", "Email", "Insurance Types", "Submitted", "Status", "", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left font-cinzel uppercase text-gray-400 whitespace-nowrap"
                      style={{ fontSize: "9px", letterSpacing: "0.15em" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((q) => {
                  const isExpanded   = expanded === q._id;
                  const fullName     = [q.firstName, q.middleName, q.lastName].filter(Boolean).join(" ");
                  const types        = q.selectedInsurance || [];
                  const visibleTypes = types.slice(0, 2);
                  const extraCount   = types.length - 2;
                  const isDeleting   = deleting === q._id;

                  return (
                    <Fragment key={q._id}>
                      <tr
                        className="border-t transition-colors duration-150"
                        style={{
                          borderColor: "rgba(1,46,114,0.06)",
                          backgroundColor: isExpanded ? "#f7f9ff" : "white",
                        }}
                      >
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-constantia text-[#012E72] font-semibold" style={{ fontSize: "14px" }}>
                            {fullName}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-constantia text-gray-500" style={{ fontSize: "13px" }}>
                            {q.dob || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-constantia text-gray-500" style={{ fontSize: "13px" }}>
                            {q.phone}
                          </span>
                        </td>
                        <td className="px-4 py-3.5" style={{ maxWidth: "180px" }}>
                          <span className="font-constantia text-gray-500 truncate block" title={q.email}
                            style={{ fontSize: "13px" }}>
                            {q.email}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-wrap gap-1">
                            {visibleTypes.map((id) => (
                              <span key={id}
                                className="font-cinzel uppercase px-2 py-0.5 rounded-full text-[#012E72] whitespace-nowrap"
                                style={{ fontSize: "7px", letterSpacing: "0.1em", backgroundColor: "rgba(0,45,181,0.08)" }}>
                                {INSURANCE_LABELS[id] || id}
                              </span>
                            ))}
                            {extraCount > 0 && (
                              <span className="font-cinzel uppercase px-2 py-0.5 rounded-full text-gray-400 whitespace-nowrap"
                                style={{ fontSize: "7px", backgroundColor: "rgba(0,0,0,0.04)" }}>
                                +{extraCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <span className="font-constantia text-gray-400" style={{ fontSize: "12px" }}>
                            {timeAgo(q.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <select
                            value={q.status}
                            disabled={updating === q._id}
                            onChange={(e) => updateStatus(q._id, e.target.value)}
                            className="font-cinzel uppercase rounded-full px-2.5 py-1 border-0 outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{
                              fontSize: "9px", letterSpacing: "0.12em",
                              color:           STATUS_CONFIG[q.status]?.color || "#6B7280",
                              backgroundColor: STATUS_CONFIG[q.status]?.bg    || "#F3F4F6",
                            }}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3.5">
                          <button
                            onClick={() => setExpanded(isExpanded ? null : q._id)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-[#012E72] hover:text-white"
                            style={{
                              backgroundColor: isExpanded ? "#012E72" : "rgba(1,46,114,0.07)",
                              color:           isExpanded ? "white"   : "#012E72",
                            }}
                            title={isExpanded ? "Collapse" : "View details"}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                              style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }}>
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          </button>
                        </td>
                        <td className="px-4 py-3.5">
                          <button
                            onClick={() => deleteQuote(q._id)}
                            disabled={isDeleting}
                            className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-200 hover:bg-red-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ backgroundColor: "rgba(239,68,68,0.07)", color: "#EF4444" }}
                            title="Delete quote"
                          >
                            {isDeleting ? (
                              <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                              </svg>
                            ) : (
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                            )}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && <ExpandedQuote q={q} />}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="font-constantia text-gray-400" style={{ fontSize: "12px" }}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="w-8 h-8 rounded-lg flex items-center justify-center font-cinzel transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#012E72] hover:text-white"
              style={{ fontSize: "11px", border: "1.5px solid rgba(1,46,114,0.12)", color: "#012E72" }}
            >
              ‹
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
              .reduce((acc, n, idx, arr) => {
                if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…");
                acc.push(n);
                return acc;
              }, [])
              .map((n, i) =>
                n === "…" ? (
                  <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center font-constantia text-gray-300"
                    style={{ fontSize: "12px" }}>…</span>
                ) : (
                  <button key={n} onClick={() => setPage(n)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center font-cinzel transition-all duration-150"
                    style={{
                      fontSize: "11px",
                      border:          `1.5px solid ${safePage === n ? "#012E72" : "rgba(1,46,114,0.12)"}`,
                      backgroundColor: safePage === n ? "#012E72" : "white",
                      color:           safePage === n ? "white"   : "#012E72",
                    }}>
                    {n}
                  </button>
                )
              )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="w-8 h-8 rounded-lg flex items-center justify-center font-cinzel transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#012E72] hover:text-white"
              style={{ fontSize: "11px", border: "1.5px solid rgba(1,46,114,0.12)", color: "#012E72" }}
            >
              ›
            </button>
          </div>
        </div>
      )}

    </div>
  );
}